import { type Dispatch, type SetStateAction } from 'react'
import { type TFunction } from 'i18next'
import { Save } from 'lucide-react'

import {
    Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Label, Select, SelectContent, SelectTrigger, SelectItem, SelectValue, Switch, Slider,
} from '@/components/ui'
import type { AiConfig } from './manage'

interface SettingsTabProps {
    aiConfig: AiConfig
    setAiConfig: Dispatch<SetStateAction<AiConfig>>
    onSaveConfig: () => void
    t: TFunction
}

export function SettingsTab({ aiConfig, setAiConfig, onSaveConfig, t }: SettingsTabProps) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    {t('helpdesk.admin.aiSettings', 'AI-configuratie')}
                </CardTitle>
                <CardDescription>
                    {t('helpdesk.admin.aiSettingsDescription', 'Configureer de parameters voor de AI-integratie')}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-2">
                            <Label htmlFor="model">{t('helpdesk.admin.model', 'AI Model')}</Label>
                            <Select
                                defaultValue={aiConfig.modelName}
                                onValueChange={(value) => setAiConfig({ ...aiConfig, modelName: value })}
                            >
                                <SelectTrigger id="model">
                                    <SelectValue placeholder={t('helpdesk.admin.selectModel', 'Selecteer model')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                                    <SelectItem value="gpt-4">GPT-4</SelectItem>
                                    <SelectItem value="claude-3-opus">Claude 3 Opus</SelectItem>
                                    <SelectItem value="claude-3-sonnet">Claude 3 Sonnet</SelectItem>
                                    <SelectItem value="mistral-large">Mistral Large</SelectItem>
                                    <SelectItem value="llama-3-70b">Llama 3 70B</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="maxTokens">{t('helpdesk.admin.maxTokens', 'Max tokens')} ({aiConfig.maxTokens})</Label>
                            <Slider
                                id="maxTokens"
                                min={256}
                                max={8192}
                                step={128}
                                value={[aiConfig.maxTokens]}
                                onValueChange={(value: number[]) => setAiConfig({ ...aiConfig, maxTokens: value[0] })}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-2">
                            <Label htmlFor="temperature">{t('helpdesk.admin.temperature', 'Temperature')} ({aiConfig.temperature})</Label>
                            <Slider
                                id="temperature"
                                min={0}
                                max={2}
                                step={0.1}
                                value={[aiConfig.temperature]}
                                onValueChange={(value: number[]) => setAiConfig({ ...aiConfig, temperature: value[0] })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="topP">{t('helpdesk.admin.topP', 'Top P')} ({aiConfig.topP})</Label>
                            <Slider
                                id="topP"
                                min={0}
                                max={1}
                                step={0.05}
                                value={[aiConfig.topP]}
                                onValueChange={(value: number[]) => setAiConfig({ ...aiConfig, topP: value[0] })}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-2">
                            <Label htmlFor="frequencyPenalty">{t('helpdesk.admin.frequencyPenalty', 'Frequency Penalty')} ({aiConfig.frequencyPenalty})</Label>
                            <Slider
                                id="frequencyPenalty"
                                min={0}
                                max={2}
                                step={0.1}
                                value={[aiConfig.frequencyPenalty]}
                                onValueChange={(value: number[]) => setAiConfig({ ...aiConfig, frequencyPenalty: value[0] })}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="presencePenalty">{t('helpdesk.admin.presencePenalty', 'Presence Penalty')} ({aiConfig.presencePenalty})</Label>
                            <Slider
                                id="presencePenalty"
                                min={0}
                                max={2}
                                step={0.1}
                                value={[aiConfig.presencePenalty]}
                                onValueChange={(value: number[]) => setAiConfig({ ...aiConfig, presencePenalty: value[0] })}
                            />
                        </div>
                    </div>

                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium mb-4">{t('helpdesk.admin.features', 'Functionaliteiten')}</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label htmlFor="documentSearch" className="text-base">
                                        {t('helpdesk.admin.documentSearch', 'Document zoeken')}
                                    </Label>
                                    <p className="text-sm text-gray-500">
                                        {t('helpdesk.admin.documentSearchDescription', 'Doorzoek geüploade documenten voor antwoorden')}
                                    </p>
                                </div>
                                <Switch
                                    id="documentSearch"
                                    checked={aiConfig.documentSearchEnabled}
                                    onCheckedChange={(checked) => setAiConfig({ ...aiConfig, documentSearchEnabled: checked })}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <Label htmlFor="webSearch" className="text-base">
                                        {t('helpdesk.admin.webSearch', 'Web zoeken')}
                                    </Label>
                                    <p className="text-sm text-gray-500">
                                        {t('helpdesk.admin.webSearchDescription', 'Zoek op het internet voor antwoorden (gebruik met voorzichtigheid)')}
                                    </p>
                                </div>
                                <Switch
                                    id="webSearch"
                                    checked={aiConfig.webSearchEnabled}
                                    onCheckedChange={(checked) => setAiConfig({ ...aiConfig, webSearchEnabled: checked })}
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <Label htmlFor="feedbackCollection" className="text-base">
                                        {t('helpdesk.admin.feedbackCollection', 'Feedback verzameling')}
                                    </Label>
                                    <p className="text-sm text-gray-500">
                                        {t('helpdesk.admin.feedbackCollectionDescription', 'Verzamel gebruikersfeedback op antwoorden')}
                                    </p>
                                </div>
                                <Switch
                                    id="feedbackCollection"
                                    checked={aiConfig.feedbackCollection}
                                    onCheckedChange={(checked) => setAiConfig({ ...aiConfig, feedbackCollection: checked })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4 pt-4 border-t">
                        <Button variant="outline">
                            {t('helpdesk.admin.resetDefaults', 'Standaardinstellingen herstellen')}
                        </Button>
                        <Button onClick={onSaveConfig}>
                            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            {t('helpdesk.admin.saveSettings', 'Instellingen opslaan')}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
} 